import { useTwoUITelemetry } from "@amx/ux-twoui-telemetry";
import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { OmitStrict } from "@amx/ux-twoui-utils";
import { DefaultButton, GroupSpacer, HighContrastSelector, IStackStyles, Stack } from "@fluentui/react";
import React, { useEffect } from "react";
import { BaseProps, validateProps } from "../../../../shared/props";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { getColorFromCorrectPalette } from "../../../../styles/colors";
import { hiddenScreenReaderElementStyles } from "../../../../styles/constants";
import { OnClickButtonProps } from "../../../Buttons/shared/props";
import { FocusZone } from "../../../FocusZone/FocusZone";
import { StackItem } from "../../../Stacks/Stack/Stack";
import { StopPropagation } from "../../../StopPropagation/StopPropagation";
import { ListItemBackgroundColor } from "../../shared/props";

export interface ListItemCanvasOnClickProps extends OmitStrict<OnClickButtonProps, "disabled"> {
    readonly pageArea: string;
}

type OnInteractEventType = React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement> | React.FocusEvent<HTMLElement>;

export interface ListItemCanvasExternalProps extends BaseProps {
    /** Props needed in order to make the list item clickable */
    readonly onClickProps?: ListItemCanvasOnClickProps;

    /** Background color for list item (overrides the one set at the list level) */
    readonly backgroundColor?: ListItemBackgroundColor;

    /** Callback function for hover, focus, or press activation */
    readonly onEnter?: (event: OnInteractEventType) => void;

    /** Callback function for hover, focus, or press deactivation */
    readonly onLeave?: (event: OnInteractEventType) => void;

    /** Optional flag to indicate that the list item's contents are disabled */
    readonly disabled?: boolean;

    /** Optional flag to indicate that the list item is in the selected state. Default is false. */
    readonly selected?: boolean;

    /**
     * **DEPRECATED**
     * Optional flag to indicate that the list item's menu is disabled
     * @deprecated menuDisabled does not work with the new ListItem experience. If you want to disable the menu in a new ListItem,
     * see the available properties of a ActionButtonFinalColumn or ActionMenuFinalColumn instead.
     */
    readonly menuDisabled?: boolean;

    /**
     * Toggle to render divider between list items
     */
    readonly showDivider?: boolean;

    /** Optional toggle to remove listitem role on ListItemCanvas. This is used primarily for creating List headers. */
    readonly removeRole?: boolean;

    /**
     * Override role for listItem in different usage, default to be "listitem"
     */
    readonly listItemRole?: "gridcell" | "listitem" | "row" | "treeitem";

    /**
     * Optional role for each cell within a row, defaults to no role
     * Defaults to gridcell if list type is DetailsList
     */
    readonly cellRole?: "gridcell";

    /**
     * Optional specification to set stack's horizontal property within ListItemCanvas, defaults to true
     */
    readonly horizontalStack?: boolean;

    /**
     * Optional custom ListItem padding that overrides the default values
     */
    readonly customListItemPadding?: string;
}

export interface ListItemCanvasProps extends ListItemCanvasExternalProps {
    readonly groupSpacerNestingDepth?: number;

    /** Padding for list item. */
    readonly padding?: string;

    /** Optional flag to indicate whether the data is focusable. Default is true. */
    readonly "data-is-focusable"?: boolean;
}

const listItemCanvasValidator = (removeRole?: boolean, listItemRole?: string): ComponentValidator => (
    () => (removeRole && listItemRole ? "Forbidden passing listItemRole when removeRole prop is true" : undefined)
);

const getListItemCanvasStyle = (
    theme: Theme,
    backgroundColor?: ListItemBackgroundColor,
    padding?: string,
    minHeight?: string,
    hasHover?: boolean,
    showDivider?: boolean,
    selected?: boolean
): IStackStyles => {
    const bgColor: ListItemBackgroundColor = backgroundColor ? backgroundColor : "neutralHigh";

    const interactiveStyles: IStackStyles["root"] = hasHover ? {
        cursor: "pointer",
        ":hover": {
            background: theme.palette.neutralLighterAlt,
            [HighContrastSelector]: {
                background: "highlight"
            }
        },
        ":active": {
            background: theme.palette.neutralLight,
            [HighContrastSelector]: {
                background: "highlight"
            }
        }
    } : undefined;

    const selectedStyles: IStackStyles["root"] = selected ? {
        backgroundColor: theme.palette.themeLighterAlt,
        border: `1px solid ${theme.palette.themeSecondary}`,
        ":hover": {
            backgroundColor: theme.palette.themeLighterAlt,
            borderColor: theme.palette.themeDark,
            [HighContrastSelector]: {
                backgroundColor: "highlight"
            }
        },
        ":active": {
            backgroundColor: theme.palette.themeLighter,
            borderColor: theme.palette.themeDark,
            [HighContrastSelector]: {
                backgroundColor: "highlight"
            }
        }
    } : undefined;

    const otherStyles: IStackStyles["root"] = {
        [HighContrastSelector]: {
            border: "1px solid WindowText"
        },
        ":focus": {
            // Adding offset for outline so it does not get cut off from parent components using 'overflow: hidden'.
            outlineOffset: "-2px"
        }
    };

    return {
        root: {
            backgroundColor: getColorFromCorrectPalette(theme, bgColor),
            padding,
            borderBottom: showDivider ? `1px solid ${theme.palette.white}` : undefined,
            minHeight,
            ...interactiveStyles,
            ...selectedStyles,
            ...otherStyles
        }
    };
};

export const listItemMinHeight = "48px";

export const ListItemCanvas: React.FC<ListItemCanvasProps> = ({
    backgroundColor,
    padding,
    children,
    groupSpacerNestingDepth,
    onClickProps,
    onEnter,
    onLeave,
    showDivider = true,
    disabled,
    selected,
    "data-is-focusable": dataIsFocusable = true,
    listItemRole,
    horizontalStack = true,
    ...otherProps
}) => {
    const twoUITelemetry = useTwoUITelemetry();
    validateProps("ListItemCanvas", otherProps);

    const theme = useTheme();

    useEffect(() => {
        validateComponent("ListItemCanvas", [listItemCanvasValidator(otherProps.removeRole, listItemRole)]);
    });

    let reportWrappedOnClickProps: ListItemCanvasProps["onClickProps"];
    if (onClickProps && !disabled) {
        reportWrappedOnClickProps = {
            ...onClickProps,
            onClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                twoUITelemetry.reportClick({
                    clickId: onClickProps["data-bi-id"],
                    pageArea: onClickProps.pageArea
                });
                onClickProps.onClick(event);
            }
        };
    }

    const getRole = () => {
        if (otherProps.removeRole) {
            return undefined;
        } else if (listItemRole) {
            return listItemRole;
        } else {
            return "listitem";
        }
    };

    return (
        <Stack
            data-is-focusable={dataIsFocusable}
            tabIndex={dataIsFocusable ? 0 : -1}
            grow={true}
            verticalAlign="center"
            styles={getListItemCanvasStyle(theme, backgroundColor, padding, listItemMinHeight, !!onClickProps && !disabled, showDivider, selected)}
            role={getRole()}
            {...otherProps}
            {...reportWrappedOnClickProps}
            aria-label={otherProps["aria-label"]}
            onMouseEnter={onEnter}
            onTouchStart={onEnter}
            onFocus={onEnter}
            onMouseLeave={onLeave}
            onTouchCancel={onLeave}
            onBlur={onLeave}
        >
            <FocusZone direction="horizontal">
                <Stack
                    verticalAlign="center"
                    horizontal={horizontalStack}
                    grow={true}
                >
                    {groupSpacerNestingDepth !== undefined && groupSpacerNestingDepth !== 0 && (
                        <StackItem>
                            <GroupSpacer count={groupSpacerNestingDepth} />
                        </StackItem>
                    )}
                    {reportWrappedOnClickProps && (
                        // This button is purposely attached to the ListItem experience to fix an accessibility issue related to
                        // announcing the interactive ability of ListItems for screen readers. Do not remove this button unless you are
                        // intending to improve the experience.
                        <StopPropagation>
                            <DefaultButton
                                text={reportWrappedOnClickProps["aria-label"]}
                                {...reportWrappedOnClickProps}
                                // To prevent duplicate ids on our pages, adding a key word to distinguish this button id from the ListItem onClick ids
                                id={reportWrappedOnClickProps.id ? `${reportWrappedOnClickProps.id}-hidden` : undefined}
                                styles={hiddenScreenReaderElementStyles}
                            />
                        </StopPropagation>
                    )}
                    <StackItem grow={true}>
                        {children}
                    </StackItem>
                </Stack>
            </FocusZone>
        </Stack>
    );
};
